import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Stack, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar, Alert, CircularProgress,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { payrollApi, PayrollPeriod } from '../../services/employeeService';
import {
  PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone, useConfirm,
} from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDuration, formatMoney, formatMonthYear, formatNumber, toNumber } from '../../utils/format';

const STATUS_FLOW: Record<string, { next: string; label: string }[]> = {
  DRAFT: [{ next: 'REVIEW', label: 'Przekaż do sprawdzenia' }],
  REVIEW: [{ next: 'APPROVED', label: 'Zatwierdź rozliczenie' }, { next: 'DRAFT', label: 'Cofnij do wersji roboczej' }],
  APPROVED: [{ next: 'PAID', label: 'Oznacz jako wypłacone' }],
  PAID: [], CANCELLED: [],
};

const PERIOD_STATUS: Record<string, { label: string; tone: PillTone }> = {
  DRAFT: { label: 'Wersja robocza', tone: 'neutral' },
  REVIEW: { label: 'Do sprawdzenia', tone: 'warning' },
  APPROVED: { label: 'Zatwierdzone', tone: 'info' },
  PAID: { label: 'Wypłacone', tone: 'success' },
  CANCELLED: { label: 'Anulowane', tone: 'danger' },
};

const PeriodStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const st = PERIOD_STATUS[status] || { label: status, tone: 'neutral' as PillTone };
  return <StatusPill label={st.label} tone={st.tone} />;
};

const periodName = (p: { year: number; month: number }) => {
  const s = formatMonthYear(new Date(p.year, p.month - 1, 1));
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const money = (v: unknown) => formatMoney(v, { decimals: 2 });

export const PayrollPeriodsPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const confirm = useConfirm();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [periods, setPeriods] = useState<PayrollPeriod[]>([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [active, setActive] = useState<PayrollPeriod | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    try {
      setPeriods(await payrollApi.listPeriods(activeSalonId));
    } finally {
      setPeriodsLoaded(true);
    }
  }, [activeSalonId]);
  useEffect(() => { load(); }, [load]);

  const openOrCreate = async () => {
    if (!activeSalonId) return;
    setLoading(true);
    const [y, m] = month.split('-').map(Number);
    try {
      const period = await payrollApi.ensurePeriod(activeSalonId, y, m);
      setActive(await payrollApi.getPeriod(period.id));
      load();
    } finally { setLoading(false); }
  };

  const generate = async () => {
    if (!active) return;
    setLoading(true);
    try { setActive(await payrollApi.generate(active.id)); setToast({ msg: 'Naliczono rozliczenia', sev: 'success' }); }
    catch (e) { setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się przeliczyć okresu. Spróbuj ponownie.', sev: 'error' }); }
    finally { setLoading(false); }
  };

  const transition = async (status: string) => {
    if (!active) return;
    try { await payrollApi.transition(active.id, status); setActive(await payrollApi.getPeriod(active.id)); load(); setToast({ msg: 'Zmieniono status', sev: 'success' }); }
    catch (e) { setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zmienić statusu. Spróbuj ponownie.', sev: 'error' }); }
  };

  const requestTransition = async (next: string) => {
    if (!active) return;
    if (next === 'APPROVED' || next === 'PAID') {
      const paid = next === 'PAID';
      const ok = await confirm({
        title: paid ? 'Oznaczyć rozliczenie jako wypłacone?' : 'Zatwierdzić rozliczenie?',
        details: <><strong>{periodName(active)}</strong> · {countLabel(active.items?.length || 0, 'pozycja', 'pozycje', 'pozycji')}</>,
        description: paid
          ? 'To ostatni krok — wypłaconego okresu nie da się już zmienić.'
          : 'Po zatwierdzeniu okresu nie da się przeliczyć ani cofnąć do wersji roboczej.',
        confirmLabel: paid ? 'Oznacz jako wypłacone' : 'Zatwierdź rozliczenie',
        tone: 'primary',
      });
      if (!ok) return;
    }
    transition(next);
  };

  const items = active?.items || [];
  const sum = (key: 'generatedRevenue' | 'grossSalary' | 'employerCost' | 'salonResult') => items.reduce((s, i) => s + toNumber(i[key]), 0);
  const totalResult = sum('salonResult');
  const canGenerate = active && (active.status === 'DRAFT' || active.status === 'REVIEW');

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Wynagrodzenia' }]}
        title="Rozliczenia miesięczne"
        description="Otwórz miesiąc, przelicz wynagrodzenia i prowadź okres od wersji roboczej aż do wypłaty."
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            type="month"
            size="small"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            inputProps={{ 'aria-label': 'Miesiąc rozliczenia' }}
            sx={{ width: 190 }}
          />
          <Button
            variant="contained"
            onClick={openOrCreate}
            disabled={loading}
            startIcon={loading && !active ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Otwórz lub utwórz okres
          </Button>
        </Stack>
      </PageHeader>

      <Stack gap={2.5}>
        {loading && (!active || items.length === 0) ? (
          <LoadingState variant="table" rows={4} />
        ) : active ? (
          <>
            {items.length > 0 && (
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                <StatTile label="Obrót pracowników" value={formatMoney(sum('generatedRevenue'))} />
                <StatTile label="Wynagrodzenia brutto" value={formatMoney(sum('grossSalary'))} />
                <StatTile label="Koszt pracodawcy" value={formatMoney(sum('employerCost'))} />
                <StatTile
                  label="Wynik salonu"
                  value={formatMoney(totalResult)}
                  valueColor={totalResult < 0 ? tokens.danger : tokens.success}
                  hint={countLabel(items.length, 'pozycja', 'pozycje', 'pozycji')}
                />
              </Box>
            )}

            <SectionCard
              padding="none"
              title={(
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" component="span">
                  <span>{periodName(active)}</span>
                  <PeriodStatusPill status={active.status} />
                </Stack>
              )}
              subtitle={active.salon?.name}
            >
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${tokens.line}` }}>
                {STATUS_FLOW[active.status]?.map((t, i) => (
                  <Button key={t.next} size="small" variant={i === 0 ? 'contained' : 'outlined'} onClick={() => requestTransition(t.next)}>
                    {t.label}
                  </Button>
                ))}
                {canGenerate && (
                  <Button size="small" variant="outlined" startIcon={<CalculateOutlinedIcon />} onClick={generate} disabled={loading}>
                    Przelicz
                  </Button>
                )}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => payrollApi.exportCsv(active.id, `rozliczenia-${active.month}-${active.year}.csv`).catch(() => setToast({ msg: 'Nie udało się pobrać pliku CSV. Spróbuj ponownie.', sev: 'error' }))}
                >
                  Eksport CSV
                </Button>
              </Stack>

              {loading ? (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress size={28} /></Box>
              ) : items.length === 0 ? (
                <EmptyState
                  compact
                  icon={<CalculateOutlinedIcon />}
                  title="Brak pozycji w tym okresie"
                  description={canGenerate
                    ? 'Kliknij „Przelicz”, aby naliczyć wynagrodzenia na podstawie czasu pracy i sprzedaży.'
                    : 'Ten okres nie ma naliczonych wynagrodzeń.'}
                />
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Pracownik</TableCell>
                        <TableCell align="right">Czas rozliczeniowy</TableCell>
                        <TableCell align="right">Obrót</TableCell>
                        <TableCell align="right">Brutto</TableCell>
                        <TableCell align="right">Koszt pracodawcy</TableCell>
                        <TableCell align="right">Wynik salonu</TableCell>
                        <TableCell align="right">Rentowność</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((i) => (
                        <TableRow key={i.id} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={1.25}>
                              <PersonAvatar firstName={i.staff?.firstName} lastName={i.staff?.lastName} seed={i.staff?.id} size={32} />
                              <Typography sx={{ fontWeight: 600, fontSize: 'inherit', whiteSpace: 'nowrap' }}>{i.staff?.firstName} {i.staff?.lastName}</Typography>
                              {i.isCorrection && <StatusPill size="small" tone="plum" noDot label="Korekta" />}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDuration(i.billableWorkMinutes)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(i.generatedRevenue)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(i.grossSalary)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(i.employerCost)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 650, color: toNumber(i.salonResult) >= 0 ? 'success.main' : 'error.main' }}>{money(i.salonResult)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatNumber(toNumber(i.profitabilityPct), 1)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          </>
        ) : periodsLoaded && periods.length === 0 ? (
          <SectionCard>
            <EmptyState
              icon={<RequestQuoteOutlinedIcon />}
              title="Nie masz jeszcze okresów rozliczeniowych"
              description="Wybierz miesiąc powyżej i kliknij „Otwórz lub utwórz okres”, aby zacząć naliczanie wynagrodzeń."
            />
          </SectionCard>
        ) : null}

        {periods.length > 0 && (
          <SectionCard title="Historia okresów" subtitle="Kliknij okres, aby go otworzyć" padding="none">
            <TableContainer>
              <Table sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Okres</TableCell>
                    <TableCell align="right">Pozycje</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" aria-label="Akcje" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      selected={active?.id === p.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={async () => setActive(await payrollApi.getPeriod(p.id))}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{periodName(p)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(p._count?.items ?? 0)}</TableCell>
                      <TableCell><PeriodStatusPill status={p.status} /></TableCell>
                      <TableCell align="right"><Button size="small" variant="text">Otwórz</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        )}
      </Stack>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default PayrollPeriodsPage;
